import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	addUserToSharingHousesHouseIdSharingUsersPostMutation,
	getSharingUserActivityHousesHouseIdSharingUsersUserIdActivityGetQueryKey,
	inviteSharingUserHousesHouseIdSharingUsersUserIdInvitePostMutation,
	removeSharingUserHousesHouseIdSharingUsersUserIdDeleteMutation,
	updateSharingUserExpiryHousesHouseIdSharingUsersUserIdExpiryPatchMutation,
	updateSharingUserRoleHousesHouseIdSharingUsersUserIdPatchMutation,
	listPropertyContactsHousesHouseIdContactsGetQueryKey,
} from '@/client/@tanstack/react-query.gen';
import type { SharingUser } from '@/client/types.gen';
import { sharingQueryKey } from '@/hooks/sharing/useSharingList';
import { toaster } from '@/components/ui/toaster';
import { Roles } from '@/utils/roleUtils';

/**
 * Mutations for managing shared users on a house via the auto-generated sharing client
 */
export const useSharingMutations = (houseId: number) => {
	const queryClient = useQueryClient();
	const invalidateSharing = () => queryClient.invalidateQueries({ queryKey: sharingQueryKey(houseId) });
	const updateCachedSharingUser = (userId: number, updater: (user: SharingUser) => SharingUser) =>
		queryClient.setQueriesData<SharingUser[]>({ queryKey: sharingQueryKey(houseId) }, (old) =>
			old?.map((user) => (user.user_id === userId ? updater(user) : user)),
		);

	// Each of these writes an event to the user's access-activity log, so refresh that user's
	// timeline (the drawer's Activity card) rather than invalidating it from every call site.
	const invalidateActivity = (userId: number) =>
		queryClient.invalidateQueries({
			queryKey: getSharingUserActivityHousesHouseIdSharingUsersUserIdActivityGetQueryKey({
				path: { house_id: houseId, user_id: userId },
			}),
		});

	const addUser = useMutation({
		...addUserToSharingHousesHouseIdSharingUsersPostMutation(),
		// The re-grant itself is logged even when the follow-up invite email fails, so refresh the
		// timeline from the add too.
		onSuccess: (data) => {
			invalidateSharing();
			invalidateActivity(data.user_id);
		},
	});

	const inviteUser = useMutation({
		...inviteSharingUserHousesHouseIdSharingUsersUserIdInvitePostMutation(),
		onSuccess: (_data, variables) => {
			invalidateSharing();
			invalidateActivity(variables.path.user_id);
		},
	});

	const updateUserRole = useMutation({
		...updateSharingUserRoleHousesHouseIdSharingUsersUserIdPatchMutation(),
		onMutate: async (variables) => {
			await queryClient.cancelQueries({ queryKey: sharingQueryKey(houseId) });
			const previousSharing = queryClient.getQueriesData<SharingUser[]>({ queryKey: sharingQueryKey(houseId) });
			updateCachedSharingUser(variables.path.user_id, (user) => ({ ...user, role: variables.body.role }));
			return { previousSharing };
		},
		onError: (_error, _variables, context) => {
			context?.previousSharing.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
			invalidateSharing();
		},
		onSuccess: (data, variables) => {
			// Avoid a blanket sharing refetch here: non-admin role responses can clear expires_at,
			// while the drawer preserves or repairs the current expiry as part of the role change.
			updateCachedSharingUser(variables.path.user_id, (user) => ({
				...user,
				...data,
				expires_at: variables.body.role === Roles.Admin ? data.expires_at : user.expires_at,
			}));
			invalidateActivity(variables.path.user_id);
		},
	});

	const removeUser = useMutation({
		...removeSharingUserHousesHouseIdSharingUsersUserIdDeleteMutation(),
		onSuccess: (_data, variables) => {
			invalidateSharing();
			invalidateActivity(variables.path.user_id);
			// Access is reflected on every contact card naming this account, so a removal changes the
			// Services list as well as the Access list.
			queryClient.invalidateQueries({
				queryKey: listPropertyContactsHousesHouseIdContactsGetQueryKey({
					path: { house_id: houseId },
				}),
			});
		},
	});

	const updateExpiry = useMutation({
		...updateSharingUserExpiryHousesHouseIdSharingUsersUserIdExpiryPatchMutation(),
		onSuccess: (_data, variables) => {
			const newExpiresAt = variables.body.expires_at ?? null;
			queryClient.setQueriesData<SharingUser[]>({ queryKey: sharingQueryKey(houseId) }, (old) =>
				old?.map((u) => (u.user_id === variables.path.user_id ? { ...u, expires_at: newExpiresAt } : u)),
			);
			invalidateActivity(variables.path.user_id);
		},
		onError: () => {
			toaster.create({ title: 'Failed to update access duration', type: 'error' });
		},
	});

	return { addUser, inviteUser, updateUserRole, removeUser, updateExpiry };
};
